/**
 * AP-37 Detailblatt O 7.1 — **Reiter Anfragen**: jede Preisanfrage mit Beleg,
 * Lieferant, Kalkulationen, Frist, beantworteten Zeilen und Status. Listen-Standard
 * (FilterBar, Spalten, Sortierung, Summe). Ein Klick öffnet die Anfrage als
 * Seitenfeld; markierte Entwürfe lassen sich nacheinander senden (6.4, O-40).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Lock, Send, Settings } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { loadUiSetting, markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings'
import { closeRequest, fetchRequests, type ListRequestsParams } from '../../api/calculation-requests.api'
import type { AnfrageStatus, RequestRow } from '../../types/calculation-requests.types'
import { AnfrageSeitenfeld, STATUS_ANFRAGE } from './AnfrageSeitenfeld'
import { AnfragenSenden, type ZuSenden } from './AnfragenSenden'

interface ColDef {
  key: string
  label: string
  align?: 'right'
  kosten?: boolean
}
const ALLE_SPALTEN: ColDef[] = [
  { key: 'supplier', label: 'Lieferant' },
  { key: 'calculations', label: 'Kalkulationen' },
  { key: 'createdAt', label: 'angelegt' },
  { key: 'requestedAt', label: 'angefragt' },
  { key: 'dueAt', label: 'Antwort bis' },
  { key: 'answered', label: 'Zeilen', align: 'right' },
  { key: 'status', label: 'Status' },
  { key: 'answerTotal', label: 'Summe', align: 'right', kosten: true },
]
const DEFAULT_KEYS = ['supplier', 'calculations', 'requestedAt', 'dueAt', 'answered', 'status', 'answerTotal']
const LAYOUT_KEY = 'columnbar_layout_calculation_requests'
interface ColumnState {
  columnKeys: string[]
  columnWidths: Record<string, number>
  sortKey: string
  sortDir: 'asc' | 'desc'
}
const VORGABE: ColumnState = { columnKeys: DEFAULT_KEYS, columnWidths: {}, sortKey: 'createdAt', sortDir: 'desc' }
const tag = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '')

function filterParams(f: Record<string, ActiveFilter>, calculationId?: string): ListRequestsParams {
  const p: ListRequestsParams = {}
  if (calculationId) p.calculationId = calculationId
  for (const [key, a] of Object.entries(f)) {
    if (!a.value) continue
    if (key === 'search') p.search = a.value
    if (key === 'status') p.status = a.value
    if (key === 'supplier') p.supplierId = a.value
    if (key === 'calculation') p.calculationId = a.value
    if (key === 'overdue') p.overdue = a.value === 'true'
    if (key === 'kind') p.kind = a.value as 'MATERIAL' | 'SERVICES'
  }
  return p
}

export function AnfragenReiter({ calculationId, neuLaden, onGeaendert }: { calculationId?: string; neuLaden?: number; onGeaendert?: () => void }) {
  const { user } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const alleSpalten = useMemo(() => ALLE_SPALTEN.filter((c) => !c.kosten || kosten), [kosten])
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})
  const [colState, setColState] = useState<ColumnState>(() => ({ ...VORGABE, ...loadUiSetting<Partial<ColumnState> | null>(LAYOUT_KEY, null) }))
  const [config, setConfig] = useState(false)
  const [rows, setRows] = useState<RequestRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [offen, setOffen] = useState<string | null>(null)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [zuSenden, setZuSenden] = useState<ZuSenden[] | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const optionen = useRef({ lieferanten: new Map<string, string>(), kalkulationen: new Map<string, string>() })
  const [optionenStand, setOptionenStand] = useState(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dragKey = useRef<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)

  const laden = useCallback(
    async (f: Record<string, ActiveFilter>, s: ColumnState) => {
      setLoading(true)
      setError(null)
      try {
        const r = await fetchRequests({ ...filterParams(f, calculationId), sortBy: s.sortKey, sortDir: s.sortDir })
        for (const a of r.data) {
          optionen.current.lieferanten.set(a.supplier.id, a.supplier.name)
          for (const k of a.calculations) optionen.current.kalkulationen.set(k.id, k.label)
        }
        setOptionenStand((x) => x + 1)
        setRows(r.data)
        setAuswahl((alt) => new Set([...alt].filter((id) => r.data.some((a) => a.id === id))))
      } catch (e) {
        setError(apiErrorText(e, 'Die Anfragen ließen sich nicht laden.'))
      } finally {
        setLoading(false)
      }
    },
    [calculationId],
  )
  useEffect(() => {
    void laden(filterValues, colState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neuLaden, calculationId])

  const filterFields = useMemo<FilterFieldDef[]>(
    () => [
      { key: 'search', label: 'Suche (Beleg, Lieferant)', type: 'text', category: 'core', operators: ['contains'] },
      {
        key: 'status',
        label: 'Status',
        type: 'select-multi',
        category: 'core',
        operators: ['is'],
        options: (Object.keys(STATUS_ANFRAGE) as AnfrageStatus[]).map((s) => ({ value: s, label: STATUS_ANFRAGE[s].label })),
      },
      { key: 'supplier', label: 'Lieferant', type: 'select', category: 'core', operators: ['is'], options: [...optionen.current.lieferanten].map(([value, label]) => ({ value, label })) },
      { key: 'calculation', label: 'Kalkulation', type: 'select', category: 'core', operators: ['is'], options: [...optionen.current.kalkulationen].map(([value, label]) => ({ value, label })) },
      { key: 'overdue', label: 'überfällig', type: 'boolean', category: 'core', operators: ['is'] },
      {
        key: 'kind',
        label: 'Art',
        type: 'select',
        category: 'core',
        operators: ['is'],
        options: [
          { value: 'MATERIAL', label: 'Material' },
          { value: 'SERVICES', label: 'Leistung (Nachunternehmer)' },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionenStand],
  )

  const onFilter = useCallback(
    (neu: Record<string, ActiveFilter>) => {
      setFilterValues(neu)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => void laden(neu, colState), 400)
    },
    [laden, colState],
  )
  const setze = (u: (p: ColumnState) => ColumnState) =>
    setColState((p) => {
      const n = u(p)
      saveUiSetting(LAYOUT_KEY, n)
      return n
    })
  const sortiere = (key: string) => {
    const n = { ...colState, sortKey: key, sortDir: colState.sortKey === key && colState.sortDir === 'asc' ? ('desc' as const) : ('asc' as const) }
    setze(() => n)
    void laden(filterValues, n)
  }
  const breite = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    const start = (e.target as HTMLElement).closest('th')?.getBoundingClientRect().width ?? 120
    const x = e.clientX
    const move = (ev: MouseEvent) => setze((p) => ({ ...p, columnWidths: { ...p.columnWidths, [key]: Math.max(60, start + ev.clientX - x) } }))
    const up = () => {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
    }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  const spalten = colState.columnKeys.map((k) => alleSpalten.find((c) => c.key === k)).filter((c): c is ColDef => !!c)
  const hinzu = alleSpalten.filter((c) => !colState.columnKeys.includes(c.key))
  const gewaehlt = rows.filter((a) => auswahl.has(a.id))
  const entwuerfe = gewaehlt.filter((a) => a.status === 'DRAFT')
  const pfeil = (key: string) =>
    colState.sortKey === key ? colState.sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-primary" /> : <ArrowDown className="h-3 w-3 text-primary" /> : <ArrowUpDown className="h-3 w-3 opacity-30" />
  const summe = rows.reduce((s, a) => s + (a.answerTotal ?? 0), 0)

  const zelle = (a: RequestRow, key: string): React.ReactNode => {
    switch (key) {
      case 'supplier':
        return (
          <span>
            {a.supplier.name}
            {a.contactName && <span className="text-xs text-muted-foreground"> · {a.contactName}</span>}
          </span>
        )
      case 'calculations':
        return a.calculations.map((k) => k.label).join(', ')
      case 'createdAt':
        return tag(a.createdAt)
      case 'requestedAt':
        return tag(a.requestedAt)
      case 'dueAt':
        return <span className={a.overdue ? 'font-semibold text-destructive' : undefined}>{tag(a.dueAt)}</span>
      case 'answered':
        return `${a.linesAnswered} / ${a.linesTotal}`
      case 'status':
        return (
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${a.overdue ? 'bg-destructive/15 text-destructive' : STATUS_ANFRAGE[a.status].klasse}`}>
            {a.overdue ? 'überfällig' : STATUS_ANFRAGE[a.status].label}
          </span>
        )
      case 'answerTotal':
        return a.answerTotal ? geldText(a.answerTotal) : ''
      default:
        return null
    }
  }

  const abschliessen = async () => {
    try {
      for (const a of gewaehlt.filter((x) => x.status !== 'CLOSED' && x.status !== 'CANCELLED')) await closeRequest(a.id)
      setAuswahl(new Set())
      await laden(filterValues, colState)
      onGeaendert?.()
    } catch (e) {
      setError(apiErrorText(e, 'Abschließen hat nicht geklappt.'))
    }
  }

  const gesamt = 2 + spalten.length + (config && hinzu.length ? 1 : 0) + 1
  return (
    <div className="space-y-4">
      <FilterBar
        availableFields={filterFields}
        value={filterValues}
        onChange={onFilter}
        layoutKey="calculation_requests"
        defaultFieldKeys={['search', 'status', 'supplier', 'overdue']}
        defaultValues={{ status: { fieldKey: 'status', operator: 'is', value: 'DRAFT,REQUESTED,ANSWERED' } }}
      />
      {gewaehlt.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">{gewaehlt.length} gewählt</span>
          {entwuerfe.length > 0 && (
            <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-primary-foreground" onClick={() => setZuSenden(entwuerfe.map((a) => ({ documentId: a.documentId, supplierName: a.supplier.name })))}>
              <Send className="h-4 w-4" /> {entwuerfe.length} Entwurf{entwuerfe.length === 1 ? '' : 'e'} senden
            </button>
          )}
          <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 hover:bg-muted" onClick={() => void abschliessen()}>
            <Lock className="h-4 w-4" /> Abschließen
          </button>
        </div>
      )}
      {hinweis && (
        <div className="flex items-start justify-between rounded-md bg-muted px-4 py-2 text-sm">
          <span>{hinweis}</span>
          <button type="button" onClick={() => setHinweis(null)} aria-label="Hinweis schließen">
            ✕
          </button>
        </div>
      )}
      <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <button
          type="button"
          onClick={() => setConfig((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md ${config ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          title="Spalten konfigurieren"
        >
          {config ? <Check size={14} /> : <Settings size={14} />}
        </button>
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" />
        {config && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={LAYOUT_KEY}
              currentValue={colState}
              onReset={(v) => {
                const n = { ...VORGABE, ...((v as Partial<ColumnState> | null) ?? {}) }
                markUiSettingApplied(LAYOUT_KEY, n)
                setColState(n)
                void laden(filterValues, n)
              }}
            />
          </div>
        )}
        {error && <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="w-8 px-2 py-1.5">
                  <input type="checkbox" aria-label="alle" checked={rows.length > 0 && rows.every((a) => auswahl.has(a.id))} onChange={(e) => setAuswahl(e.target.checked ? new Set(rows.map((a) => a.id)) : new Set())} />
                </th>
                <th className="relative px-3 py-1.5 text-left font-medium">
                  <button type="button" onClick={() => sortiere('documentNumber')} className="flex items-center gap-1 hover:text-primary">
                    Beleg {pfeil('documentNumber')}
                  </button>
                </th>
                {spalten.map((c) => (
                  <th
                    key={c.key}
                    className={`relative select-none px-3 py-1.5 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'} ${config ? 'cursor-grab' : ''} ${dragOver === c.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                    draggable={config}
                    onDragStart={config ? () => (dragKey.current = c.key) : undefined}
                    onDragOver={
                      config
                        ? (e) => {
                            e.preventDefault()
                            setDragOver(c.key)
                          }
                        : undefined
                    }
                    onDrop={
                      config
                        ? () => {
                            const q = dragKey.current
                            setDragOver(null)
                            if (!q || q === c.key) return
                            setze((p) => {
                              const k = [...p.columnKeys]
                              k.splice(k.indexOf(q), 1)
                              k.splice(k.indexOf(c.key), 0, q)
                              return { ...p, columnKeys: k }
                            })
                          }
                        : undefined
                    }
                    style={colState.columnWidths[c.key] ? { width: colState.columnWidths[c.key] } : undefined}
                  >
                    <div className={`flex items-center gap-1 ${c.align === 'right' ? 'justify-end' : ''}`}>
                      <button type="button" onClick={() => sortiere(c.key === 'answered' ? 'linesAnswered' : c.key)} className="flex items-center gap-1 hover:text-primary">
                        {c.label} {pfeil(c.key === 'answered' ? 'linesAnswered' : c.key)}
                      </button>
                      {config && (
                        <button type="button" className="ml-1 text-muted-foreground hover:text-destructive" onClick={() => setze((p) => ({ ...p, columnKeys: p.columnKeys.filter((k) => k !== c.key) }))}>
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40" onMouseDown={(e) => breite(e, c.key)} />
                  </th>
                ))}
                {config && hinzu.length > 0 && (
                  <th className="px-2 py-1.5">
                    <select
                      className="rounded border border-border bg-background px-1 py-0.5 text-xs"
                      defaultValue=""
                      onChange={(e) => {
                        const k = e.target.value
                        if (k) setze((p) => ({ ...p, columnKeys: [...p.columnKeys, k] }))
                        e.target.value = ''
                      }}
                    >
                      <option value="">+ Spalte</option>
                      {hinzu.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </th>
                )}
                <th className="w-4" />
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={gesamt} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Keine Anfragen — im Reiter Material Zeilen wählen und „Preisanfrage erzeugen".
                  </td>
                </tr>
              )}
              {rows.map((a, i) => (
                <tr key={a.id} className={`cursor-pointer border-b border-border last:border-0 ${i % 2 === 0 ? 'bg-background' : 'bg-muted/20'} ${offen === a.id ? 'bg-primary/5' : ''} hover:bg-accent/30`} onClick={() => setOffen(a.id)}>
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={a.documentNumber ?? a.supplier.name}
                      checked={auswahl.has(a.id)}
                      onChange={(e) =>
                        setAuswahl((alt) => {
                          const n = new Set(alt)
                          if (e.target.checked) n.add(a.id)
                          else n.delete(a.id)
                          return n
                        })
                      }
                    />
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/correspondence/${a.documentId}`} className="text-primary hover:underline">
                      {a.documentNumber ?? 'Entwurf'}
                    </Link>
                  </td>
                  {spalten.map((c) => (
                    <td key={c.key} className={`whitespace-nowrap px-3 py-1.5 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {zelle(a, c.key)}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
              {rows.length > 0 && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td />
                  <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{rows.length} Anfragen</td>
                  {spalten.map((c) => (
                    <td key={c.key} className={`px-3 py-1.5 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {c.key === 'answerTotal' ? geldText(summe) : ''}
                    </td>
                  ))}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {offen && (
        <AnfrageSeitenfeld
          id={offen}
          onClose={() => {
            setOffen(null)
            void laden(filterValues, colState)
          }}
          onGeaendert={() => onGeaendert?.()}
        />
      )}
      {zuSenden && (
        <AnfragenSenden
          belege={zuSenden}
          onFertig={(e) => {
            setZuSenden(null)
            setAuswahl(new Set())
            void laden(filterValues, colState)
            onGeaendert?.()
            setHinweis([`${e.gesendet} gesendet`, e.uebersprungen.length ? `nicht gesendet: ${e.uebersprungen.join(', ')}` : '', ...e.fehler].filter(Boolean).join(' · '))
          }}
        />
      )}
    </div>
  )
}
